import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { XMarkIcon } from '@heroicons/react/16/solid'
import { Button } from '../ui/button'
import React, { useState } from 'react'
import useAppStore from '@/store/useAppStore'
import { TextField } from '../ui/TextField'
import SimpleComboBox from '../SimpleComboBox'
import { Description } from '../ui/Description'
import _ from 'lodash'
import useGetVaults from '@/services/api/vault/useGetVaults'
import { useController, useForm } from 'react-hook-form'
import useGetPrearchiveVault from '@/services/api/vault/useGetPrearchiveVault'
import useArchiveVault from '@/services/api/vault/useArchiveVault'
import useOpenModalWindow from '../../hooks/useOpenModalWndow'
import { ChevronLeft } from 'lucide-react'
import Field from '../ui/Field'
import { Label } from '../ui/Label'

export interface ArchiveVaultValues {
  new_income_default_vault: any
  new_expense_default_vault: any
  transfer_balance_to: any
}

export default function ArchiveVault() {
  const archivedVault = useAppStore((state) => state.archivedVault)
  const { data: vaults } = useGetVaults()
  const { handleSubmit, control } = useForm<ArchiveVaultValues>({
    defaultValues: {},
  })
  const modalWindowState = useAppStore((state) => state.modalWindowState)
  const toggleArchiveStep = useAppStore((state) => state.toggleArchiveStep)
  const archiveStep = useAppStore((state) => state.archiveStep)
  const setArchiveStep = useAppStore((state) => state.setArchiveStep)
  const [isNextEnabled, setIsNextEnabled] = useState(false)
  const { data: prearchiveVault } = useGetPrearchiveVault(archivedVault.id)
  const { action: archiveVault } = useArchiveVault(archivedVault.id)
  const { closeModal } = useOpenModalWindow()

  const vaultsFlat = _.flatten(
    vaults?.map((vault) => {
      return vault.is_group ? vault.vaults : [vault]
    }),
  )

  const determineType = () => {
    if (
      prearchiveVault?.requires_income_default &&
      prearchiveVault?.requires_expense_default
    ) {
      return 'allCategories'
    } else if (prearchiveVault?.requires_income_default) {
      return 'forIncome'
    } else if (prearchiveVault?.requires_expense_default) {
      return 'forExpense'
    } else if (prearchiveVault?.requires_transfer) {
      return 'requiresTransfer'
    }
    setArchiveStep('second')
    return 'default'
  }

  const type = determineType()

  const config = {
    allCategories: {
      text_first_part:
        'You are about to archive the Main Vault for both the "Money In" and "Money Out" categories.',
      text_second_part: 'new Vaults for each of these categories',
      text_third_part:
        'Additionally, choose a Vault to transfer the remaining funds from the Vault you are archiving.',
    },
    forIncome: {
      text_first_part:
        'You are about to archive the Main Vault for  “Money In” category.',
      text_second_part: 'new Vault for this category',
      text_third_part:
        'Additionally, choose a Vault to transfer the remaining funds from the Vault you are archiving.',
    },
    forExpense: {
      text_first_part:
        'You are about to archive the Main Vault for  “Money Out” category.',
      text_second_part: 'new Vault for this category',
      text_third_part:
        'Additionally, choose a Vault to transfer the remaining funds from the Vault you are archiving.',
    },
    requiresTransfer: {
      text_first_part: 'The Vault you want to archive has a balance.',
      text_second_part: 'a Vault to which this balance will be transferred',
      text_third_part: '',
    },
    default: {
      text_first_part: 'Archive Vault: This will move the Vault to the Archive folder',
      text_second_part: 'It won\'t be available for use but can be restored later.',
      text_third_part: '',
    }
  }

  const text_first_part = config[type]?.text_first_part
  const text_second_part = config[type]?.text_second_part
  const text_third_part = config[type]?.text_third_part

  const { field: vaultForMoneyInField } = useController({
    control: control,
    name: 'new_income_default_vault',
    shouldUnregister: true,
  })

  const { field: vaultForMoneyOutField } = useController({
    control: control,
    name: 'new_expense_default_vault',
    shouldUnregister: true,
  })

  const { field: destinationVault } = useController({
    control: control,
    name: 'transfer_balance_to',
    shouldUnregister: true,
  })

  React.useEffect(() => {
    function isValid() {
      if (prearchiveVault?.requires_transfer && !destinationVault.value) {
        return false
      }
      if (
        prearchiveVault?.requires_income_default &&
        !vaultForMoneyInField.value
      ) {
        return false
      }
      if (
        prearchiveVault?.requires_expense_default &&
        !vaultForMoneyOutField.value
      ) {
        return false
      }
      return true
    }

    setIsNextEnabled(isValid())
  }, [vaultForMoneyInField, vaultForMoneyOutField, destinationVault])

  const onSubmit = (data) => {
    const {
      new_income_default_vault,
      new_expense_default_vault,
      transfer_balance_to,
    } = data
    archiveVault({
      ...data,
      new_income_default_vault: new_income_default_vault?.id,
      new_expense_default_vault: new_expense_default_vault?.id,
      transfer_balance_to: transfer_balance_to?.id,
    })
    closeModal()
    setArchiveStep('first')
  }

  return (
    <form noValidate className=" flex flex-col">
      <Dialog open={modalWindowState === 'opened'} modal={true}>
        <DialogContent
          onWheel={(e) => e.stopPropagation()}
          className=" max-h-[432px]"
        >
          <DialogHeader>
            <DialogTitle>Archive Vault - {archivedVault?.name}</DialogTitle>
            <DialogClose>
              <XMarkIcon
                onClick={() => {
                  closeModal()
                  setArchiveStep('first')
                }}
              />
            </DialogClose>
          </DialogHeader>

          {archiveStep === 'first' && (
            <>
              {!text_first_part || !text_second_part ? (
                <div>Loading...</div>
              ) : (
                <DialogBody className="flex flex-col gap-4">
                  <p className="text-[14px]">
                    {text_first_part} Please select {text_second_part}{' '}
                    {text_third_part}
                  </p>
                  {(type === 'forIncome' || type === 'allCategories') && (
                    <div className="mb-3 ">
                      <Field>
                        <Label className="text-sm">
                          Main Vault for Money In
                        </Label>
                        <SimpleComboBox
                          placeholder="Please select a new Main Vault for Money In"
                          items={vaultsFlat.filter(
                            (item) => item.id !== archivedVault.id,
                          )}
                          selected={vaultForMoneyInField.value}
                          setSelected={vaultForMoneyInField.onChange}
                          getOptionLabel={(item) => (
                            <div>
                              <p>{item.name}</p>
                              <Description>{item.description}</Description>
                            </div>
                          )}
                          getSelectedLabel={(item) => item.name}
                          getValue={(item) => item.id}
                          clearable
                          commandListClassName="w-[400px]"
                        />
                      </Field>
                    </div>
                  )}

                  {(type === 'forExpense' || type === 'allCategories') && (
                    <div className="mb-3">
                      <TextField>
                        <TextField.Label className="text-sm">
                          Main Vault for Money Out
                        </TextField.Label>
                        <SimpleComboBox
                          placeholder="Please select a new Main Vault for Money Out"
                          items={vaultsFlat.filter(
                            (item) => item.id !== archivedVault.id,
                          )}
                          selected={vaultForMoneyOutField.value}
                          setSelected={vaultForMoneyOutField.onChange}
                          getOptionLabel={(item) => (
                            <div>
                              <p>{item.name}</p>
                              <Description>{item.description}</Description>
                            </div>
                          )}
                          getSelectedLabel={(item) => item.name}
                          getValue={(item) => item.id}
                          clearable
                          commandListClassName="w-[400px]"
                        />
                      </TextField>
                    </div>
                  )}

                  {prearchiveVault?.requires_transfer && (
                    <div className="mb-3">
                      <TextField>
                        <TextField.Label className="text-sm">
                          Destination Vault
                        </TextField.Label>
                        <SimpleComboBox
                          placeholder="Select the Vault to transfer the remaining funds to"
                          items={vaultsFlat.filter(
                            (item) => item.id !== archivedVault.id,
                          )}
                          selected={destinationVault.value}
                          setSelected={destinationVault.onChange}
                          getOptionLabel={(item) => (
                            <div>
                              <p>{item.name}</p>
                              <Description>{item.description}</Description>
                            </div>
                          )}
                          getSelectedLabel={(item) => item.name}
                          getValue={(item) => item.id}
                          clearable
                          commandListClassName="w-[400px]"
                        />
                      </TextField>
                    </div>
                  )}
                </DialogBody>
              )}

              <DialogFooter>
                <DialogClose asChild>
                  <Button
                    onClick={() => {
                      closeModal()
                      setArchiveStep('first')
                    }}
                    type="button"
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                </DialogClose>

                <Button
                  disabled={!isNextEnabled}
                  type="button"
                  onClick={toggleArchiveStep}
                >
                  Next
                </Button>
              </DialogFooter>
            </>
          )}

          {archiveStep === 'second' && (
            <>
              <DialogBody>
                <p>
                  Are you sure you want to archive this Vault? All scheduled
                  transactions associated with this Vault will be deleted.
                </p>
              </DialogBody>

              <DialogFooter>
                <div className="flex w-full justify-between">
                  <Button
                    onClick={toggleArchiveStep}
                    type="button"
                    variant="outline"
                  >
                    <ChevronLeft />
                    Back
                  </Button>
                  <div>
                    <DialogClose asChild>
                      <Button
                        onClick={() => {
                          closeModal()
                          setArchiveStep('first')
                        }}
                        variant="secondary"
                        className="mr-2"
                      >
                        Cancel
                      </Button>
                    </DialogClose>
                    <Button
                      type="submit"
                      onClick={handleSubmit(onSubmit)}
                      variant="destructive"
                    >
                      Archive
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </form>
  )
}
